import Fuse from 'fuse.js'
import { foldText, tokenize, tokenizeMany } from './textNormalize'

// Build memoised Fuse.js indices from Redux-derived collections.
//
// Goal: stay as close to the Algolia behaviour as we can without re-implementing
// BM25 from scratch. The keys, weights, and Fuse options below are derived from
// `TEMP_LOCAL_SEARCH_INDEXES.md` (which itself is distilled from
// `WEB_ALGOLIA_USAGE_ANALYSIS.md` + `API_ALGOLIA_USAGE_ANALYSIS.md`).
//
// Key local-vs-Algolia field mapping
// ----------------------------------
// Algolia attribute       | Local Redux attribute
// ------------------------|----------------------------------
// repo.title              | repo.name
// repo.authorObject.first | repo.author.firstName
// repo.authorObject.last  | repo.author.lastName
// set._questionBodies     | set.questions[].body          (or pre-tokenized)
// set._questionChoices    | set.questions[].choices[].body
// question._questionChoices | question.choices[].body
//
// The set/question discriminator on hits stays implicit and identical to
// Algolia: a set hit has `hit.questions` populated, a question hit has
// `hit.choices` populated and no `hit.questions`. The `notArchived` filter
// mirrors the Algolia pre-filter `archived!=1 AND parentArchived!=1` but stays
// permissive across boolean and numeric flag conventions used in Redux.
//
// ────────────────────────────────────────────────────────────────────────────
// PRE_TOKENIZE_INDEX
// ────────────────────────────────────────────────────────────────────────────
// Fuse.js operates on raw character sequences via the Bitap algorithm. With a
// short query like "math", that lets it stitch the answer out of bigrams
// scattered across a sentence (e.g. "ma" inside "**ma**n" plus "th" inside
// "**th**e") even when no actual word starts with or is a typo of the query.
// Algolia, by contrast, tokenizes each field on word boundaries before its
// BM25 + typo-tolerance pass — so it never matches across token boundaries.
//
// Setting `PRE_TOKENIZE_INDEX = true` mirrors that behaviour:
//   1. Each indexed field is replaced (in a parallel `*Tokens` projection)
//      by an array of lower-cased word tokens (split on `\W+`).
//   2. Fuse keys point at those `*Tokens` arrays. Fuse natively scores per
//      element of an array, so a query is matched against one token at a
//      time — no cross-token character soup.
//   3. The Fuse base options are tightened to roughly mirror Algolia's
//      per-token typo tolerance (1 typo for queries of length ≥ 4, exact
//      prefix only for length < 4).
//
// Setting `PRE_TOKENIZE_INDEX = false` keeps the previous "raw string +
// dot-notation nested keys" behaviour. Use it when you want maximum recall
// and don't mind the cross-token matching.
const PRE_TOKENIZE_INDEX = true

// ─── Tokenization helpers ──────────────────────────────────────────────────
//
// `tokenize` / `tokenizeMany` come from the shared `textNormalize` module so
// indexing and querying use one Unicode-aware, diacritic-folding, CJK-aware
// implementation. See that file for the full rationale.

// Each prepare* function adds a `*Tokens` projection of the searchable
// fields onto a clone of the record. The original fields are left
// untouched so any downstream rendering (titles, descriptions, getDocumentType,
// etc.) keeps working unchanged. When PRE_TOKENIZE_INDEX is false these
// are no-ops.

function prepareRepo(repo) {
  if (!PRE_TOKENIZE_INDEX) return repo
  const author = repo && repo.author
  return {
    ...repo,
    nameTokens: tokenize(repo && repo.name),
    descriptionTokens: tokenize(repo && repo.description),
    authorFirstNameTokens: tokenize(author && author.firstName),
    authorLastNameTokens: tokenize(author && author.lastName),
  }
}

function prepareFolder(folder) {
  if (!PRE_TOKENIZE_INDEX) return folder
  return {
    ...folder,
    nameTokens: tokenize(folder && folder.name),
  }
}

function prepareSet(set) {
  if (!PRE_TOKENIZE_INDEX) return set
  const questions = (set && Array.isArray(set.questions)) ? set.questions : []
  const bodies = questions.map((q) => (q ? q.body : ''))
  const choices = []
  for (let i = 0; i < questions.length; i += 1) {
    const q = questions[i]
    if (q && Array.isArray(q.choices)) {
      for (let j = 0; j < q.choices.length; j += 1) {
        const c = q.choices[j]
        if (c) choices.push(c.body)
      }
    }
  }
  return {
    ...set,
    nameTokens: tokenize(set && set.name),
    questionBodyTokens: tokenizeMany(bodies),
    questionChoiceTokens: tokenizeMany(choices),
  }
}

function prepareQuestion(question) {
  if (!PRE_TOKENIZE_INDEX) return question
  const choices = (question && Array.isArray(question.choices)) ? question.choices : []
  const choiceBodies = choices.map((c) => (c ? c.body : ''))
  return {
    ...question,
    bodyTokens: tokenize(question && question.body),
    choiceTokens: tokenizeMany(choiceBodies),
  }
}

// ─── Fuse base options ─────────────────────────────────────────────────────
//
// `ignoreLocation: true` is the most important one — it prevents Fuse from
// penalising matches that occur in the middle of a string, which Algolia's
// BM25 also does not penalise.
//
// `includeMatches: true` makes Fuse emit per-key match metadata for every
// hit (which configured key matched, the actual field value, and the
// [start,end] character indices of the matched substrings). The matches are
// surfaced through `__fuseMatches` on each hit so devtools and the
// searchReporter comparison can answer "why was this entity included?".
//
// Tighter `threshold` + `minMatchCharLength` are paired with tokenization:
// once Fuse is operating per token, those values map almost directly to
// Algolia's per-token typo budget (`minWordSizefor1Typo: 4`,
// `minWordSizefor2Typos: 8`) without bigram-soup matches surviving.

const FUSE_OPTIONS_BASE = PRE_TOKENIZE_INDEX ? {
  threshold: 0.25,
  distance: 200,
  minMatchCharLength: 1,
  useExtendedSearch: false,
  includeScore: true,
  includeMatches: true,
  ignoreLocation: true,
  findAllMatches: false,
} : {
  threshold: 0.35,
  distance: 200,
  minMatchCharLength: 1,
  useExtendedSearch: false,
  includeScore: true,
  includeMatches: true,
  ignoreLocation: true,
  findAllMatches: false,
}

// repos — TopNav only. Weights mirror the spec; field names are remapped to
// the local Redux shape (see header comment) and either point at the raw
// field or its tokenized projection depending on PRE_TOKENIZE_INDEX.
const REPOS_KEYS = PRE_TOKENIZE_INDEX ? [
  { name: 'nameTokens', weight: 3 },
  { name: 'descriptionTokens', weight: 1 },
  { name: 'authorFirstNameTokens', weight: 1 },
  { name: 'authorLastNameTokens', weight: 1 },
] : [
  { name: 'name', weight: 3 },
  { name: 'description', weight: 1 },
  { name: 'author.firstName', weight: 1 },
  { name: 'author.lastName', weight: 1 },
]

const REPOS_OPTIONS = { ...FUSE_OPTIONS_BASE, keys: REPOS_KEYS }

// folders — `name` is the only meaningful text field on the folder document.
const FOLDERS_KEYS = [
  { name: PRE_TOKENIZE_INDEX ? 'nameTokens' : 'name', weight: 1 },
]

const FOLDERS_OPTIONS = { ...FUSE_OPTIONS_BASE, keys: FOLDERS_KEYS }

// sets_questions — single physical Fuse instance with the union of the per-
// entity key lists from the spec. Sets and questions never share these field
// names (sets have `name` + `questions[].*`; questions have `body` +
// `choices[].*`), so missing-field contributions are 0 and the per-entity
// rankings stay intact while keeping the index unified — exactly how the
// `sets_questions` Algolia index works.
const SETS_QUESTIONS_KEYS = PRE_TOKENIZE_INDEX ? [
  { name: 'nameTokens', weight: 3 },
  { name: 'questionBodyTokens', weight: 2 },
  { name: 'questionChoiceTokens', weight: 1 },
  { name: 'bodyTokens', weight: 3 },
  { name: 'choiceTokens', weight: 1 },
] : [
  { name: 'name', weight: 3 },
  { name: 'questions.body', weight: 2 },
  { name: 'questions.choices.body', weight: 1 },
  { name: 'body', weight: 3 },
  { name: 'choices.body', weight: 1 },
]

const SETS_QUESTIONS_OPTIONS = { ...FUSE_OPTIONS_BASE, keys: SETS_QUESTIONS_KEYS }

// Default Fuse base options (scalar knobs, sans per-domain keys). Exported so
// the internal search-tuning sandbox can show/reset them as the editable
// content Fuse options.
export const CONTENT_FUSE_BASE_OPTIONS = FUSE_OPTIONS_BASE

const cache = new WeakMap()

function notArchived(item) {
  return item && !item.archived && !item.parentArchived
}

// memoisedFuse caches a Fuse instance keyed on the input `list` reference +
// `options`. When PRE_TOKENIZE_INDEX is on, each filtered record is mapped
// through `prepare` (a domain-specific tokenizer) before being indexed.
function memoisedFuse(list, options, prepare) {
  if (!Array.isArray(list)) return new Fuse([], options)
  const cached = cache.get(list)
  if (cached && cached.options === options) return cached.fuse
  let prepared = list.filter(notArchived)
  if (typeof prepare === 'function') prepared = prepared.map(prepare)
  const fuse = new Fuse(prepared, options)
  cache.set(list, { options, fuse })
  return fuse
}

/**
 * Build per-domain Fuse indices from Redux collections.
 *
 * @param {Object} input
 * @param {Array}  input.sets
 * @param {Array}  input.questions
 * @param {Array}  input.folders
 * @param {Array}  input.repos
 * @returns {{ repos: Fuse, folders: Fuse, setsQuestions: Fuse }}
 */
export function buildFuseIndices({
  sets, questions, folders, repos,
}) {
  return {
    repos: memoisedFuse(repos || [], REPOS_OPTIONS, prepareRepo),
    folders: memoisedFuse(folders || [], FOLDERS_OPTIONS, prepareFolder),
    setsQuestions: buildSetsQuestionsIndex(sets, questions),
  }
}

const setsQuestionsCache = new WeakMap()

function buildSetsQuestionsIndex(sets, questions) {
  const setsList = Array.isArray(sets) ? sets : []
  const questionsList = Array.isArray(questions) ? questions : []
  const cacheKey = setsList
  const cached = setsQuestionsCache.get(cacheKey)
  if (cached && cached.questions === questionsList) return cached.fuse
  const combined = [
    ...setsList.filter(notArchived).map(prepareSet),
    ...questionsList.filter(notArchived).map(prepareQuestion),
  ]
  const fuse = new Fuse(combined, SETS_QUESTIONS_OPTIONS)
  setsQuestionsCache.set(cacheKey, { questions: questionsList, fuse })
  return fuse
}

function toHit({ item, score, matches }) {
  return {
    ...item,
    objectID: item.id,
    __localOrigin: true,
    __fuseScore: score,
    __fuseMatches: matches,
  }
}

const NOOP = () => {}

function emptyIndexResult() {
  return {
    hits: [], hitCount: 0, hasMore: false, loadMore: NOOP,
  }
}

// Run one Fuse index unbounded (so we can compute `hitCount`), slice for the
// returned page, and shape the result identically to AlgoliaSearchProvider's
// per-index result so the same UI components can render either side.
function runIndex(fuse, query, limitPerIndex) {
  if (!query || !fuse) return emptyIndexResult()
  // Fuse never normalizes the query itself, so fold it here to line up with
  // the folded tokens in the index (accent-insensitive + CJK-aware matching).
  const all = fuse.search(foldText(query))
  const hitCount = all.length
  const slice = (typeof limitPerIndex === 'number' && limitPerIndex > 0) ?
    all.slice(0, limitPerIndex) :
    all
  return {
    hits: slice.map(toHit),
    hitCount,
    // Local indices are not paginated — Fuse returns every match in one shot,
    // so `hasMore` is always false and `loadMore` is a no-op. Both are kept
    // on the shape so a reused IndexHitList can call them safely.
    hasMore: false,
    loadMore: NOOP,
  }
}

/**
 * Run a query against every Fuse index and return per-index results in the
 * same shape as `AlgoliaSearchProvider`'s `indexResults`:
 *
 *   {
 *     repos:          { hits, hitCount, hasMore, loadMore },
 *     folders:        { hits, hitCount, hasMore, loadMore },
 *     sets_questions: { hits, hitCount, hasMore, loadMore },
 *   }
 *
 * `limitPerIndex` mirrors Algolia's per-index `hitsPerPage`. TopNav passes 4
 * (the same cap as Algolia's `multipleQueries` per index); Add-to-Class passes
 * a high number (effectively no cap on `sets_questions`). The `hitCount`
 * always reflects the total number of matches before the limit so the page
 * header can render "N total" badges that align with Algolia.
 *
 * @param {ReturnType<typeof buildFuseIndices>} indices
 * @param {string} query
 * @param {number} [limitPerIndex=10]
 */
export function searchPerFuseIndex(indices, query, limitPerIndex = 10) {
  return {
    repos: runIndex(indices.repos, query, limitPerIndex),
    folders: runIndex(indices.folders, query, limitPerIndex),
    // eslint-disable-next-line camelcase
    sets_questions: runIndex(indices.setsQuestions, query, limitPerIndex),
  }
}

/**
 * Convenience wrapper around `searchPerFuseIndex` that returns the flat
 * concatenation of every index's hits (repos → folders → sets_questions).
 * Kept for legacy call sites and for any consumer that doesn't care about
 * per-index slicing.
 *
 * @param {ReturnType<typeof buildFuseIndices>} indices
 * @param {string} query
 * @param {number} [limitPerIndex=10]
 * @returns {Array<Object>}
 */
export function searchAcrossFuseIndices(indices, query, limitPerIndex = 10) {
  if (!query) return []
  const perIndex = searchPerFuseIndex(indices, query, limitPerIndex)
  return [
    ...perIndex.repos.hits,
    ...perIndex.folders.hits,
    ...perIndex.sets_questions.hits,
  ]
}

/**
 * Run a query against a single Fuse index and return Algolia-shaped flat
 * hits. Useful when a UI surface only wants one domain.
 *
 * @param {Fuse} fuse
 * @param {string} query
 * @param {number} [limit=10]
 * @returns {Array<Object>}
 */
export function searchSingleFuseIndex(fuse, query, limit = 10) {
  if (!query || !fuse) return []
  return fuse.search(foldText(query), { limit }).map(toHit)
}

/**
 * Build fresh (un-memoised) per-domain Fuse content indices using a custom set
 * of base options merged with the hardcoded per-domain keys. Exists only for
 * the internal search-tuning sandbox so the team can tweak the base options
 * (threshold, distance, minMatchCharLength, ignoreLocation, …) and preview the
 * effect. The per-domain `keys` stay fixed because they target the internal
 * tokenized field projections.
 *
 * @param {Object} collections  { sets, questions, folders, repos }
 * @param {Object} [baseOptions] Fuse base options (defaults to CONTENT_FUSE_BASE_OPTIONS)
 * @returns {{ repos: Fuse, folders: Fuse, setsQuestions: Fuse }}
 */
export function buildContentFuseIndices({
  sets, questions, folders, repos,
}, baseOptions) {
  const base = (baseOptions && typeof baseOptions === 'object') ? baseOptions : FUSE_OPTIONS_BASE
  const repoDocs = (Array.isArray(repos) ? repos : []).filter(notArchived).map(prepareRepo)
  const folderDocs = (Array.isArray(folders) ? folders : []).filter(notArchived).map(prepareFolder)
  const setsQuestionsDocs = [
    ...(Array.isArray(sets) ? sets : []).filter(notArchived).map(prepareSet),
    ...(Array.isArray(questions) ? questions : []).filter(notArchived).map(prepareQuestion),
  ]
  return {
    repos: new Fuse(repoDocs, { ...base, keys: REPOS_KEYS }),
    folders: new Fuse(folderDocs, { ...base, keys: FOLDERS_KEYS }),
    setsQuestions: new Fuse(setsQuestionsDocs, { ...base, keys: SETS_QUESTIONS_KEYS }),
  }
}
